<template>
  <b-row class="state-table">
    <b-overlay
      :show="showOverlay"
      variant="transparent"
      opacity="0.9"
      rounded="sm"
    >
      <b-col class="d-flex align-items-center w-100 mb-1 justify-content-end">
        <direction-filter
          v-if="useDirectionFilter"
          class="d-flex flex-row align-items-center mr-1"
          min-width="400px"
          @change="onChangeDirectionFilter"
        ></direction-filter>
        <search-filter
          class="search-filter"
          @input="onInputSearchText"
        ></search-filter>
        <b-button
          v-if="showAddItemButton"
          variant="success"
          class="ml-1"
          @click="addItem"
        >Добавить</b-button>
        <slot name="checkbox-filter"></slot>
      </b-col>
      <b-col cols="12">
        <b-table
          :ref="refName"
          :selected-variant="isDark ? 'custom-active' : 'active'"
          :fields="fields"
          :items="items"
          responsive
          show-empty
          outlined
          :caption-top="!!toolbarActions"
          :empty-text="emptyText"
          :select-mode="selectMode"
          :selectable="selectable"
          @row-clicked="onRowClicked"
          @row-selected="onRowSelected"
        >
          <template #table-caption>
            <div
              v-if="toolbarActions"
              class="toolbar border"
            >
              <div>{{ toolbarTitle }}</div>
              <div
                v-if="selectedItems.length > 1"
                class="d-flex align-items-center justify-content-end"
              >
                <div
                  v-for="(action, idx) in toolbarActions"
                  :key="idx"
                  :title="action.label"
                  class="cursor-pointer"
                  @click.prevent.stop="action.callback(selectedItems)"
                >
                  <b-img
                    :src="action.icon"
                    :alt="action.label"
                    width="20"
                    height="20"
                  ></b-img>
                </div>
              </div>
            </div>
          </template>
          <template #cell(name)="row">
            <not-enough-material-badge
              :value="row.item.name"
              :not-enough="
                (row.field.checkServiceMaterialAvailability && serviceHasNotEnoughMaterial(row.item))
                || (
                  row.field.checkComplexMaterialAvailability
                  && complexHasNotEnoughMaterial(row.item.dental_services, row.item.system_column_count)
                )
              "
              badge-class="ml-1"
              badge-title="Имеется израсходованный материал"
            ></not-enough-material-badge>
          </template>
          <template #cell(supplier_material_name)="row">
            <not-enough-material-badge
              :value="row.item.supplier_material_name"
              :not-enough="
                (
                  row.field.checkMaterialAvailability
                  && isNotEnoughMaterialByLot(row.item.system_column_count, row.item)
                )
              "
              badge-class="ml-1"
              badge-title="Израсходован"
            ></not-enough-material-badge>
          </template>
          <template #cell(own_template)="row">
            <div
              v-if="currentUserId === row.item.creator"
              class="d-flex w-100 justify-content-center"
              title="Мой шаблон"
            >
              <b-badge
                class="profile-badge"
                variant="light-success"
              >
                <feather-icon
                  icon="CheckCircleIcon"
                ></feather-icon>
              </b-badge>
            </div>
          </template>
          <template #cell(system_column_count)="row">
            <b-form-input
              type="number"
              v-model="row.item.system_column_count"
              :disabled="!itemExistInSelectedItems(row.item.id)"
              :formatter="positiveNumberFormatter"
            ></b-form-input>
          </template>
          <template #cell(total_price)="row">
            <template v-if="row.field.recalculateComplexTotalPrice">
              <div class="d-flex flex-row align-items-center">
                <template v-if="row.item.calc_discount_percent">
                  <span
                    :style="{'text-decoration': 'line-through'}"
                    class="font-small-2 mr-50"
                  >{{
                    formatPrice(
                      (recalculateComplexTotalPrice(row.item, row.field.complexKey) * 100) / (100 - row.item.calc_discount_percent),
                      true
                    )
                  }}</span>
                  <b-badge
                    class="profile-badge"
                    variant="light-success"
                  >
                    <span>{{ formatPrice(recalculateComplexTotalPrice(row.item, row.field.complexKey), true) }}</span>
                  </b-badge>
                </template>
                <span v-else>
                  {{ formatPrice(recalculateComplexTotalPrice(row.item, row.field.complexKey), true) }}
                </span>
                <not-enough-material-badge
                  v-if="checkComplexMaterial(row.item, row.field.complexKey)"
                  not-enough
                  badge-class="ml-50"
                  :badge-title="getComplexWarnings(row.item, row.field.complexKey)"
                ></not-enough-material-badge>
              </div>
            </template>
            <template v-else-if="row.field.recalculateServiceTotalPrice">
              <div class="d-flex flex-row align-items-center">
                <span>{{ formatPrice(recalculateServiceTotalPrice(row.item, row.field.serviceKey), true) }}</span>
                <not-enough-material-badge
                  v-if="checkServiceMaterial(row.item, row.field.serviceKey)"
                  not-enough
                  badge-class="ml-50"
                  :badge-title="getServiceWarnings(row.item, row.field.serviceKey)"
                ></not-enough-material-badge>
              </div>
            </template>
            <span v-else>{{ formatPrice(row.item.total_price, true) }}</span>
          </template>
          <template #cell(is_main)="row">
            <template v-if="row.field.isSwitcher">
              <b-form-checkbox
                v-model="row.item.is_main"
                inline
                name="is-main"
                @change="$emit('changed-is-main-switcher', row.item)"
              ></b-form-checkbox>
            </template>
            <span v-else>{{ row.item.is_main ? 'Да' : 'Нет' }}</span>
          </template>
          <template #cell(actions)="row">
            <div class="d-flex align-items-center justify-content-center">
              <template
                v-if="rowActions"
                v-for="(action, idx) in rowActions"
              >
                <div
                  v-if="row.item.hasOwnProperty(action.show_condition) && row.item[action.show_condition]"
                  :title="action.label"
                  class="cursor-pointer"
                  :class="{'ml-1': idx && rowActions.length > 1}"
                  @click.prevent.stop="action.callback(row)"
                >
                  <b-img
                    :src="action.icon"
                    :alt="action.label"
                    width="20"
                    height="20"
                  ></b-img>
                </div>
              </template>
              <b-icon-pencil
                v-if="showEditItemButton"
                variant="primary"
                title="Редактировать"
                class="mr-1 cursor-pointer"
                :class="{'ml-1': !!rowActions}"
                @click="editItem(row)"
              ></b-icon-pencil>
              <b-icon-trash
                v-if="showDeleteItemButton"
                variant="danger"
                title="Удалить"
                class="cursor-pointer"
                :class="{'ml-1': !!rowActions && !showEditItemButton}"
                @click="deleteItem(row)"
              ></b-icon-trash>
            </div>
          </template>
        </b-table>
      </b-col>
      <b-col cols="12">
        <b-pagination
          v-model="currentPage"
          class="mt-1"
          align="right"
          :total-rows="totalCount"
          :per-page="limit"
          @change="onPageChanged"
        ></b-pagination>
      </b-col>
    </b-overlay>
  </b-row>
</template>

<script>

import {
  BButton, BCol, BFormInput, BIconCartPlus, BIconPencil, BPagination, BRow, BTable, BOverlay, BIconTrash, BBadge,
  BFormCheckbox, BImg,
} from 'bootstrap-vue'
import StateTableParams from '@/views/components/state-table/StateTableParams'
import { ref, inject, computed } from 'vue'
import { getId } from '@/composables/currentUser'
import SearchFilter from '@/views/components/search-filter/SearchFilter.vue'
import store from '@/store'
import NotEnoughMaterialBadge from '@/views/components/not-enough-material-badge/NotEnoughMaterialBadge.vue'
import {
  serviceHasNotEnoughMaterial, needRecalculateServicePrice, calcServiceTotalPrice, getServiceMaterialWarnings,
} from '@/composables/serviceHelper'
import {
  calcComplexTotalPrice,
  complexHasNotEnoughMaterial,
  getComplexServiceMaterialWarnings,
  needRecalculateComplexPrice,
} from '@/composables/complex'
import { isNotEnoughMaterialByLot } from '@/composables/materialHelper'
import DirectionFilter from '@/views/components/filters/direction-filter/DirectionFilter.vue'

export default {
  name: 'StateTable',
  components: {
    DirectionFilter, BImg, NotEnoughMaterialBadge, SearchFilter, BFormCheckbox, BBadge, BButton, BOverlay,
    BTable, BFormInput, BPagination, BRow, BCol, BIconPencil, BIconCartPlus, StateTableParams, BIconTrash
  },
  props: {
    selectable: {
      type: Boolean,
      default: () => false,
    },
    selectMode: {
      type: String,
      default: () => StateTableParams.stateTableSelectMode.single,
    },
    emptyText: {
      type: String,
      default: () => StateTableParams.stateTableEmptyText,
    },
    itemsLoadMethodName: {
      type: String,
      required: true,
    },
    itemsLoadMethodResourceId: {
      type: Number,
      required: false,
    },
    itemsGetterName: {
      type: String,
      required: true,
    },
    itemsCountGetterName: {
      type: String,
      required: true,
    },
    refName: {
      type: String,
      required: true,
    },
    selectedRows: {
      type: Array,
      required: false,
      default: () => [],
    },
    fields: {
      type: Array,
      required: true,
    },
    itemsLoadMethodParams: {
      type: Object,
      required: false,
    },
    showAddItemButton: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    showEditItemButton: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    showDeleteItemButton: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    needReloadData: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    useDirectionFilter: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  emits: ['changed-is-main-switcher', 'items-selected', 'add-item', 'edit-item', 'delete-item', 'data-loaded'],
  watch: {
    itemsLoadMethodName: {
      async handler() {
        this.currentPage = 1
        await this.fetchData()
      },
    },
    needReloadData: {
      async handler(val) {
        if (val) {
          this.currentPage = 1
          await this.fetchData()
        }
      }
    },
    needRefresh: {
      async handler(val) {
        if (val) {
          this.currentPage = 1
          await this.fetchData()
        }
      }
    }
  },
  data() {
    return {
      filter: null,
      pageChanged: false,
      currentPage: 1,
      showOverlay: false,
      limit: 10,
    }
  },
  updated() {
    this.selectRows()
  },
  computed: {
    items() {
      let items = this.$store.getters[this.itemsGetterName] ? this.$store.getters[this.itemsGetterName] : []
      if (this.systemCountColumnExist() && items.length) {
        items = items.map(item => {
          let systemColumnCount = item.system_column_count ? item.system_column_count : 1
          if (this.selectedItems.length) {
            const index = this.selectedItems.findIndex(selectedItem => selectedItem.id === item.id)
            systemColumnCount = index > -1 ? this.selectedItems[index].system_column_count : systemColumnCount
          }
          item.system_column_count = systemColumnCount
          return item
        })
      }
      return items
    },
    totalCount() {
      return this.$store.getters[this.itemsCountGetterName] ? this.$store.getters[this.itemsCountGetterName] : 0
    },
    isDark() {
      return this.$store.state.appConfig.layout.skin === 'dark'
    },
  },
  async created() {
    this.selectedItems = this.selectedRows
    await this.fetchData()
  },
  methods: {
    systemCountColumnExist() {
      return this.fields.findIndex(field => field.key === 'system_column_count') > -1
    },
    async fetchData() {
      if (this.itemsLoadMethodName && this.canMakeRequestForSearch) {
        let search = null
        let params = {}
        const queryParams = {
          limit: this.limit,
          offset: this.currentPage === 1 ? 0 : (this.currentPage * this.limit) - this.limit
        }
        if (this.filter) {
          queryParams.search = this.filter
          search = this.filter
        }
        if (this.selectedDirections.length) {
          queryParams.direction_ids = this.selectedDirections.map(sd => sd.id).join(',')
        }
        if (this.itemsLoadMethodResourceId) {
          params = {
            id: this.itemsLoadMethodResourceId,
            queryParams: queryParams
          }
        } else if (this.itemsLoadMethodParams) {
          params = {...queryParams, ...this.itemsLoadMethodParams}
          if (this.itemsLoadMethodParams.search && queryParams.search) {
            params.search = `${this.itemsLoadMethodParams.search} ${queryParams.search}`
          }
        } else {
          params = queryParams
        }
        this.showOverlay = true
        this.canMakeRequestForSearch = false
        await this.$store.dispatch(this.itemsLoadMethodName, params).finally(() => {
          this.showOverlay = false
          this.$emit('data-loaded')
          this.canMakeRequestForSearch = true
          if (this.needRefresh) {
            store.commit('application/SET_STATE_TABLE_REFRESH_STATUS', false)
          }
          if ((search || this.filter) && search !== this.filter) {
            this.fetchData()
          }
        })
      }
    },
    async onPageChanged(page) {
      this.pageChanged = true
      this.currentPage = page
      this.canMakeRequestForSearch = true
      await this.fetchData()
    },
    onRowClicked(item) {
      if (
        item.hasOwnProperty('selectable') && !item.selectable
        && this.selectedRows.findIndex(sr => sr.id === item.id) === -1
      ) {
        this.unselectItemId = item.id
      }
    },
    onRowSelected(items) {
      if (this.unselectItemId) {
        const idx = this.items.findIndex(i => i.id === this.unselectItemId)
        this.unselectItemId = null
        this.$refs[this.refName].unselectRow(idx)
        return
      }

      if (!this.pageChanged || items.length) {
        this.pageChanged = false
        if (this.selectedItems.length) {
          this.selectedItems = this.selectedItems.map(si => {
            const existingItem = items.find(i => i.id && si.id && i.id === si.id)
            return existingItem ? existingItem : si
          })
        }
        if (this.selectMode === StateTableParams.stateTableSelectMode.single) {
          this.selectedItems = items
        } else if (items && items.length) {
          const selectedItemsLength = this.selectedItems.length
          if (selectedItemsLength) {
            let itemFound = false
            items.map(selectedItemOnPage => {
              this.selectedItems.map(sI => {
                if (sI.id === selectedItemOnPage.id && !itemFound) {
                  itemFound = true
                }
              })
              if (!itemFound) {
                this.selectedItems.push(selectedItemOnPage)
              }
              itemFound = false
            })
            if (selectedItemsLength === this.selectedItems.length) {
              let unselectedItems = []
              let itemFound = false
              this.items.map(itemOnPage => {
                items.map(selectedItemOnPage => {
                  if (itemOnPage.id === selectedItemOnPage.id  && !itemFound) {
                    itemFound = true
                  }
                })
                if (!itemFound) {
                  unselectedItems.push(itemOnPage)
                }
                itemFound = false
              })
              unselectedItems.map(unselectedItemOnPage => {
                this.selectedItems = this.selectedItems.filter(
                  selectedItem => selectedItem.id === unselectedItemOnPage.id ? null : selectedItem
                )
              })
            }
          } else {
            this.selectedItems = items
          }
        }
        this.$emit('items-selected', this.selectedItems)
      } else {
        this.pageChanged = false
      }
    },
    itemExistInSelectedItems(id) {
      return this.selectedItems.findIndex(item => item.id === id) > -1
    },
    selectRows() {
      let itemFound = false
      this.items.map((itemOnPage, key) => {
        this.selectedItems.map(selectedItem => {
          if (itemOnPage.id === selectedItem.id  && !itemFound) {
            itemFound = true
            this.$refs[this.refName].selectRow(key)
          }
        })
        itemFound = false
      })
    },
    addItem() {
      this.$emit('add-item')
    },
    editItem(row) {
      this.$emit('edit-item', row)
    },
    deleteItem(row) {
      this.$emit('delete-item', row)
    },
    recalculateComplexTotalPrice(item, complexKey) {
      const complex = complexKey ? item[complexKey] : item
      return calcComplexTotalPrice(complex)
    },
    recalculateServiceTotalPrice(item, serviceKey) {
      const service = serviceKey ? item[serviceKey] : item
      return calcServiceTotalPrice(service)
    },
    async onInputSearchText(event) {
      this.filter = event
      this.currentPage = 1
      await this.fetchData()
    },
    async onChangeDirectionFilter(e) {
      this.selectedDirections = e
      this.currentPage = 1
      await this.fetchData()
    }
  },
  setup() {
    const currentUserId = getId()
    const selectedDirections = ref([])
    const selectedItems = ref([])
    const canMakeRequestForSearch = ref(true)
    const unselectItemId = ref(null)
    const toolbarActions = inject('stateTableToolbarActions', null)
    const rowActions = inject('stateTableRowActions', null)
    const toolbarTitle = computed(() => `Выбрано записей: ${selectedItems.value.length}`)
    const needRefresh = computed(() => store.getters['application/stateTableNeedRefresh'])

    const checkServiceMaterial = (item, serviceKey) => {
      const service = serviceKey ? item[serviceKey] : item
      return needRecalculateServicePrice(service)
    }
    const getServiceWarnings = (item, serviceKey) => {
      const service = serviceKey ? item[serviceKey] : item
      return getServiceMaterialWarnings(service).join(', ')
    }
    const checkComplexMaterial = (item, complexKey) => {
      const complex = complexKey ? item[complexKey] : item
      return needRecalculateComplexPrice(complex)
    }
    const getComplexWarnings = (item, complexKey) => {
      const complex = complexKey ? item[complexKey] : item
      return getComplexServiceMaterialWarnings(complex).join(', ')
    }

    return {
      currentUserId,
      selectedDirections,
      selectedItems,
      canMakeRequestForSearch,
      unselectItemId,
      toolbarActions,
      toolbarTitle,
      rowActions,
      needRefresh,

      serviceHasNotEnoughMaterial,
      complexHasNotEnoughMaterial,
      isNotEnoughMaterialByLot,
      checkComplexMaterial,
      getComplexWarnings,
      checkServiceMaterial,
      getServiceWarnings,
    }
  }
}
</script>

<style lang='scss'>

@import '@/core/scss/base/components/variables-dark';
@import '@/assets/scss/variables/variables';

.state-table {

  .b-overlay-wrap {
    width: 100%;
  }

  table {
    caption {
      padding: 0;

      .toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: .5rem 1rem;
        background: $table-head-bg;
      }
    }

    tbody {
      tr {
        td {
          &.date {
            width: 220px;
          }
        }
      }
    }
  }
}

.dark-layout {
  .state-table {
    table {
      caption {
        .toolbar {
          background: $theme-dark-table-header-bg;
        }
      }
    }
  }
}

</style>
